import dayjs from 'dayjs';
import { UploadSummary } from '../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import { UploadSortOrderEnum } from '../../scansSlice';

/**
 * Sorts the customer uploads summaries based on the specified upload order.
 *
 * This function creates a shallow copy of the provided array to avoid mutating the original.
 * It then sorts the array according to the specified upload order.
 *
 * For UploadSortOrderEnum.newest, the function sorts summaries such that entries with more recent
 * collection dates appear first. For UploadSortOrderEnum.percentComplete, it sorts entries in descending
 * order of overall percent uploaded.
 *
 * @param uploadOrder - The order in which to sort the upload summaries.
 * @param custUploadsSummaries - An array of upload summary objects to be sorted.
 * @returns A sorted array of upload summary objects, or a shallow copy of the original array if no matching order is found.
 */
export function sortCustomerUploadsSummary(
  uploadOrder: UploadSortOrderEnum,
  custUploadsSummaries: UploadSummary[]
): UploadSummary[] {
  // Create a shallow copy of the array to avoid mutating the original
  const sortableCopy = [...custUploadsSummaries];

  let sortFn: (custA: UploadSummary, custB: UploadSummary) => number = () => 0;
  switch (uploadOrder) {
    case UploadSortOrderEnum.newest:
      sortFn = (custA: UploadSummary, custB: UploadSummary) => {
        return dayjs(custB.latestCollectionDate).isAfter(
          dayjs(custA.latestCollectionDate)
        )
          ? 1
          : -1;
      };
      break;

    case UploadSortOrderEnum.percentComplete:
      sortFn = (custA: UploadSummary, custB: UploadSummary) => {
        return custB.overallPercentUploaded - custA.overallPercentUploaded;
      };
      break;

    default:
      return sortableCopy;
  }
  return sortableCopy.sort(sortFn);
}
